/* The one place in the browser that reads the WHOLE `interactions` collection.

   useInteractions.js reads the handful of records for one open drawer, live.
   This hook is the other shape: every record, once, on demand, so the ⌘K
   palette can search message bodies. Slice 3 reuses it rather than writing a
   second loader — if you find yourself about to add another
   getDocs(collection(db, "interactions")) anywhere, add it here instead.

   Three properties that matter:

   - Lazy. Nothing is fetched until load() is called, which the palette only
     does once a query is 3 characters or longer. A CRM session that never
     opens the palette never pays for it.
   - Session-cached, in the shared makeSessionCache (sessionCache.js), so the
     second and every later load() is free and two components mounting at once
     share one read. Cleared by a reload, by __clearCrmCaches() on sign-out,
     and by losing the role.
   - Admin-only. The Firestore rule on `interactions` is the actual boundary;
     the role check here is a courtesy so a crew or viewer session does not
     fire a request that is going to come back permission-denied. Never treat
     it as the security control. */
import { useCallback, useEffect, useRef, useState } from "react";
import { db, collection, getDocs } from "../../firebase.js";
import { makeSessionCache, makeRetryGate, RETRY_AFTER_FAILURE_MS } from "./sessionCache.js";

const COLLECTION = "interactions";
const ADMIN_ROLES = new Set(["admin", "superadmin"]);

/* Re-exported so the existing importers and tests keep one import path. */
export { makeRetryGate, RETRY_AFTER_FAILURE_MS };

async function fetchAll() {
  const snap = await getDocs(collection(db, COLLECTION));
  // The document id wins over any `id` field inside the document, the same
  // rule useInteractions.js follows.
  return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
}

const store = makeSessionCache(fetchAll, { label: "[useAllInteractions]" });

/** Test seam — the fixture and unit tests replace the fetch, and any test that
 *  touched the cache resets it. Not used by app code. */
export function __setInteractionsCache(records) {
  if (records === null) store.clear();
  else store.seed(records);
}

/** Drop the session copy of this collection. Called from the sign-out path in
 *  src/App.jsx: every email body the palette pulled is still in memory, and a
 *  cached copy must not outlive the session that was entitled to it. */
export function __clearCrmCaches() {
  store.clear();
}

/** Test seam: is anything cached right now? */
export function __hasCache() {
  return store.has();
}

/**
 * @param role normalized role from jobAccess() — "admin" | "superadmin" | "crew" | "viewer"
 * @returns {{ records, loaded, loading, error, allowed, load }}
 *   records  every interaction, or [] until loaded
 *   loaded   true once a load has settled (including a load that failed)
 *   allowed  whether this role may ask at all
 *   load     idempotent; safe to call on every keystroke
 */
export function useAllInteractions(role) {
  const allowed = ADMIN_ROLES.has(role);
  const [records, setRecords] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  /* Losing the role drops what this session already read. A demotion mid-
     session, or a sign-out that keeps the app mounted, must not leave message
     bodies sitting in a component's state. */
  const allowedRef = useRef(allowed);
  useEffect(() => { allowedRef.current = allowed; }, [allowed]);
  useEffect(() => {
    if (allowed) return;
    // The shared store goes too: a cached copy of message bodies must not
    // outlive the right to read them, and must not be served to a later role
    // without a fresh, rules-checked read.
    store.clear();
    setRecords([]);
    setLoaded(false);
    setLoading(false);
    setError(null);
  }, [allowed]);

  const load = useCallback(() => {
    if (!allowed) return;
    const pending = store.load();
    if (!pending) return;           // a recent load failed; wait out the backoff
    // Every caller that attaches to an in-flight read waits visibly, not just
    // the one that started it.
    if (store.inFlight) setLoading(true);
    pending.then(({ records: rs, error: err }) => {
      if (!alive.current || !allowedRef.current) return; // unmounted, or demoted while reading
      setRecords(rs);
      // A retry that succeeds clears the error the earlier failure left behind.
      setError(err || null);
      setLoaded(true);
      setLoading(false);
    });
  }, [allowed]);

  return { records, loaded, loading, error, allowed, load };
}

export default useAllInteractions;
